import {
  NotificationPreferenceCategory,
  NotificationPreferencesDto,
  NotificationPreferenceSubcategory,
  NotificationPreferenceSubcategoryDto,
} from "../types";
import { PreferenceStaticFieldMapper } from "./static-field-mappers";

export class PreferencesFactory extends PreferenceStaticFieldMapper {
  private industrial: NotificationPreferenceSubcategoryDto[];
  private natural: NotificationPreferenceSubcategoryDto[];
  private pollution: NotificationPreferenceSubcategoryDto[];
  private infrastructure: NotificationPreferenceSubcategoryDto[];
  private misc: NotificationPreferenceSubcategoryDto[];
  private props: NotificationPreferenceCategory[];
  private ref: NotificationPreferencesDto;

  constructor(private readonly dto: NotificationPreferencesDto) {
    super();
    this.ref = this.dto;
    this.industrial = this.dto.industrial.slice().sort(this.sortKeys);
    this.natural = this.dto.natural.slice().sort(this.sortKeys);
    this.pollution = this.dto.pollution.slice().sort(this.sortKeys);
    this.infrastructure = this.dto.infrastructure.slice().sort(this.sortKeys);
    this.misc = this.dto.misc.slice().sort(this.sortKeys);
    this.props = Object.keys(this.dto).map((category) =>
      this.categoryDtoToProps(category as keyof NotificationPreferencesDto),
    );
  }

  sortKeys(
    a: NotificationPreferenceSubcategoryDto,
    b: NotificationPreferenceSubcategoryDto,
  ) {
    return a.topic.localeCompare(b.topic);
  }

  getCategoryDisplayTitle(category: keyof NotificationPreferencesDto) {
    return category.substring(0, 1).toUpperCase() + category.substring(1);
  }

  subcategoryDtoToProps(
    subcategories: NotificationPreferenceSubcategoryDto[],
  ): NotificationPreferenceSubcategory[] {
    return subcategories.map(
      (subcategory: NotificationPreferenceSubcategoryDto) => {
        const fields = this.getFieldMap(subcategory.type, subcategory.topic);
        return {
          ...subcategory,
          ...fields,
          preferences: this.getPreferences(subcategory),
        };
      },
    );
  }
  categoryDtoToProps(
    category: keyof NotificationPreferencesDto,
  ): NotificationPreferenceCategory {
    const partialCategoryProps = {
      title: this.getCategoryDisplayTitle(category),
      id: category,
      subcategories: this.subcategoryDtoToProps(this[category]),
    };

    return {
      ...partialCategoryProps,
      allSelected: partialCategoryProps.subcategories.every((x) =>
        x.preferences.every((y) => y.value),
      ),
    };
  }

  subcategoryPropsToDto(subcategory: NotificationPreferenceSubcategory) {
    return {
      title: subcategory.title,
      topic: subcategory.topic,
      type: subcategory.type,
      email_enabled: subcategory.preferences.find(
        (x) => x.name === "email_enabled",
      )?.value as boolean,
      push_notifications: subcategory.preferences.find(
        (x) => x.name === "push_notifications",
      )?.value as boolean,
    };
  }
  categoryPropsToDto(categories: NotificationPreferenceCategory[]) {
    return categories.reduce((acc, category) => {
      acc[category.id] = category.subcategories.map((subcategory) => {
        return {
          ...this.subcategoryPropsToDto(subcategory),
        };
      });
      return acc;
    }, {} as NotificationPreferencesDto);
  }

  fetchProps() {
    return this.props;
  }

  convertToDto() {
    return this.categoryPropsToDto(this.props);
  }

  handlePreferenceChange(
    category: keyof NotificationPreferencesDto,
    subcategory: string,
    key: "email_enabled" | "push_notifications",
    value: boolean,
  ) {
    const newCategory = this.props
      .filter((a) => a.id === category)
      .map((b) => ({
        ...b,
        subcategories: b.subcategories.map((c) =>
          c.topic === subcategory
            ? {
                ...c,
                preferences: c.preferences.map((d) => ({
                  ...d,
                  value: d.name === key ? value : d.value,
                })),
              }
            : c,
        ),
      }));

    return this.mergeNewCategory(newCategory[0]);
  }

  private mergeNewCategory(newCategory: NotificationPreferenceCategory) {
    const newProps: any[] = [];
    for (var i = 0; i < this.props.length; i++) {
      if (this.props[i].id === newCategory.id) {
        newProps.push(newCategory);
      } else {
        newProps.push(this.props[i]);
      }
    }
    this.props = newProps;
    return this.props;
  }

  handleSelectAllCategoryPreferences(
    category: keyof NotificationPreferencesDto,
    value: boolean,
  ) {
    const newCategory = this.props
      .filter((x) => x.id === category)
      .map((x) => ({
        ...x,
        allSelected: value,
        subcategories: x.subcategories.map((y) => ({
          ...y,
          preferences: y.preferences.map((z) => ({
            ...z,
            value,
          })),
        })),
      }));
    return this.mergeNewCategory(newCategory[0]);
  }
}
